/**
 * Browser entry point for Langton's Ant Gallery client
 * Exports factory functions for creating pages
 * Requirements: 1.1-1.6, 3.1-3.8
 */

import { GalleryPage } from './pages/gallery-page.js';
import { SimulationPage } from './pages/simulation-page.js';
import { MusicPlayer } from './components/music-player.js';
import { MapPage, type MapPageOptions } from './maps/map-page.js';
import { MapsAdminPage, type MapsAdminPageOptions } from './maps/maps-admin-page.js';

// Re-export types and utilities that might be needed
export { GalleryPage, SimulationPage, MusicPlayer, MapPage, MapsAdminPage };

/**
 * Create and initialize the gallery page
 * Requirements: 1.1-1.6
 * 
 * @param container - DOM element to mount the gallery
 * @param wsUrl - WebSocket server URL
 * @param galleryMusicUrl - Optional URL for background music
 * @returns GalleryPage instance
 */
export function createGalleryPage(
  container: HTMLElement,
  wsUrl: string,
  galleryMusicUrl?: string
): GalleryPage {
  const page = new GalleryPage(container, wsUrl, galleryMusicUrl);
  page.loadSimulations();
  // Load visible hyperbolic maps into the same grid (Req 11.1).
  page.loadMaps();
  return page;
}

/**
 * Create and initialize the simulation page
 * Requirements: 3.1-3.8
 * 
 * @param container - DOM element to mount the simulation
 * @param simulationId - ID of the simulation to display
 * @param wsUrl - WebSocket server URL
 * @returns SimulationPage instance
 */
export function createSimulationPage(
  container: HTMLElement,
  simulationId: string,
  wsUrl: string
): SimulationPage {
  return new SimulationPage(container, simulationId, wsUrl);
}

/**
 * Create and initialize the interactive hyperbolic map page
 * Requirements: 4, 5, 10.2, 7.4
 *
 * Assembles renderer, navigation, legend and loads map data via
 * `GET /api/maps/:id`, then triggers the first render. The returned promise
 * resolves once the map data has been loaded and the subsystems assembled;
 * callers may catch it to show a load error.
 *
 * @param container - DOM element to mount the map
 * @param mapId - ID of the map to display
 * @param options - optional API base, fetch function and legend panel side
 * @returns object with the MapPage instance and the load() promise
 */
export function createMapPage(
  container: HTMLElement,
  mapId: string,
  options?: MapPageOptions
): { page: MapPage; loaded: Promise<unknown> } {
  const page = new MapPage(container, mapId, options);
  const loaded = page.isWebGLAvailable()
    ? page.load()
    : Promise.reject(new Error('WebGL недоступен: карта не может быть отображена'));
  // After the map data loads, probe for an admin session and (if present) enable
  // editing tools — Requirements 7.1, 7.4. Failures are non-fatal for viewing.
  if (page.isWebGLAvailable()) {
    loaded.then(() => page.checkAdminSession()).catch(() => undefined);
  }
  return { page, loaded };
}

/**
 * Create and initialize the maps admin page (`/mapsadmin`)
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 2.1, 1.8
 *
 * Builds the admin DOM (list, login/form modals), probes for an existing admin
 * session and either shows the map list or the login modal. The returned promise
 * resolves once initialization completes.
 *
 * @param container - DOM element to mount the admin page
 * @param options - optional API base, fetch function and credential storage
 * @returns object with the MapsAdminPage instance and the init() promise
 */
export function createMapsAdminPage(
  container: HTMLElement,
  options?: MapsAdminPageOptions
): { page: MapsAdminPage; ready: Promise<void> } {
  const page = new MapsAdminPage(container, options);
  const ready = page.init();
  return { page, ready };
}

/**
 * Create a standalone music player
 * Requirements: 1.6, 3.8
 * 
 * @returns MusicPlayer instance
 */
export function createMusicPlayer(): MusicPlayer {
  return new MusicPlayer();
}

// Make functions available globally for HTML scripts
if (typeof window !== 'undefined') {
  (window as any).LangtonClient = {
    createGalleryPage,
    createSimulationPage,
    createMapPage,
    createMapsAdminPage,
    createMusicPlayer,
    GalleryPage,
    SimulationPage,
    MapPage,
    MapsAdminPage,
    MusicPlayer
  };
}
